import { JSXElementConstructor, memo, ReactElement } from 'react';

import { useController, useFormContext } from 'react-hook-form';

import { Text, Group, Switch, Radio, Flex, FlexProps } from '@ui';

import classes from './SwitcherQuestion.module.css';

interface Option {
  label: string;
  value: string | number | boolean;
  labelRight?: string;
  disabled?: boolean;
}

export interface SwitcherQuestionProps extends FlexProps {
  name: string;
  options: [Option, Option];
  label: string | ReactElement<any, string | JSXElementConstructor<any>>;
  disabled?: boolean;
  // todo : check if need to keep supporting this prop once all forms are migrated to adaptive form
  showBullet?: boolean;
  switcherType?: 'switch' | 'radios';
  bottomDivider?: boolean; // To add a divider at the bottom of the switch field only in the summary page
  showCard?: boolean;
  infoToolTip?: string | ReactElement<any, string | JSXElementConstructor<any>>;
  revalidateFields?: string[];
}

const SwitcherQuestion: React.FC<SwitcherQuestionProps> = memo(
  ({
    name,
    options,
    label,
    disabled,
    showBullet,
    switcherType = 'radios',
    revalidateFields,
    ...flexProps
  }: SwitcherQuestionProps) => {
    const { field, fieldState } = useController({ name });
    const { trigger } = useFormContext();

    const { onChange: _onChange, onBlur } = field;
    const { error } = fieldState;

    const handleChange = (value: Option['value'] | undefined) => {
      _onChange(value);
      onBlur();
      if (revalidateFields?.length) trigger(revalidateFields);
    };

    return switcherType === 'radios' ? (
      <Flex
        justify="space-between"
        align={{ base: 'center', sm: 'flex-start', lg: 'center' }}
        wrap="nowrap"
        w="100%"
        {...flexProps}
      >
        <Group gap={0} w="100%">
          {showBullet && <div className={classes.bullet} />}
          {typeof label === 'string' ? (
            <Text className={classes.label}>{label}</Text>
          ) : (
            label
          )}
        </Group>
        <Group w={140} wrap="nowrap" justify="flex-end" style={{ flexShrink: 0 }}>
          <Radio.Group
            value={field.value === undefined ? undefined : String(field.value)}
            onChange={(value) => {
              // Convert string value back to the type expected by the form
              const optionValue = options.find(
                (opt) => String(opt.value) === value
              )?.value;
              handleChange(optionValue);
            }}
            radiosWrapper="Group"
            radiosWrapperProps={{ wrap: 'nowrap', justify: 'flex-end' }}
            options={options.map((option) => ({
              value: String(option.value),
              label: option.label,
              disabled: option.disabled || disabled,
              color: option.value ? 'success' : 'danger'
            }))}
            size="md"
            error={error?.message}
            color="success"
            classNames={{ error: classes.error }}
          />
        </Group>
      </Flex>
    ) : (
      <Flex
        justify="space-between"
        align="center"
        wrap="nowrap"
        w="100%"
        {...flexProps}
      >
        <Group gap={0} w="100%">
          {showBullet && <div className={classes.bullet} />}
          <Text fz={15} c="secondary">
            {label}
          </Text>
        </Group>
        <Group w={140} wrap="nowrap" justify="flex-end">
          <Text>{options.find((opt) => opt.value === false)?.label || ''}</Text>
          <Switch
            checked={field.value === true}
            onChange={(event) => {
              handleChange(event.currentTarget.checked);
            }}
            disabled={disabled}
            labelPosition="right"
            label={options.find((opt) => opt.value === true)?.label || ''}
            error={error?.message}
          />
        </Group>
      </Flex>
    );
  }
);

export { SwitcherQuestion };
